import axios from 'axios';

// In production the backend serves the React build from the same origin, so
// API_BASE is empty and all requests are relative. In local dev set
// REACT_APP_API_URL to the backend's URL (e.g. http://localhost:3001).
const API_BASE = process.env.REACT_APP_API_URL || '';

// ─── CSRF token plumbing ───────────────────────────────────────────────────
// The backend issues a per-session CSRF token. We fetch it lazily and cache
// it in module scope; every mutating request includes it in X-CSRF-Token.
// If the server rejects with 403 (token rotated), we refetch once and retry.

let csrfToken = null;
let csrfFetchPromise = null;

async function fetchCsrfToken() {
  if (csrfFetchPromise) return csrfFetchPromise;
  csrfFetchPromise = axios
    .get(`${API_BASE}/api/csrf-token`, { withCredentials: true })
    .then(r => { csrfToken = r.data.csrfToken; return csrfToken; })
    .finally(() => { csrfFetchPromise = null; });
  return csrfFetchPromise;
}

async function ensureCsrfToken() {
  if (csrfToken) return csrfToken;
  return fetchCsrfToken();
}

function isMutating(method) {
  const m = (method || 'get').toLowerCase();
  return m === 'post' || m === 'put' || m === 'delete' || m === 'patch';
}

function attachCsrfInterceptor(instance) {
  instance.interceptors.request.use(async (config) => {
    if (isMutating(config.method)) {
      const token = await ensureCsrfToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers['X-CSRF-Token'] = token;
      }
    }
    return config;
  });

  instance.interceptors.response.use(
    r => r,
    async (err) => {
      const status = err.response?.status;
      const config = err.config;
      // CSRF token may have rotated; refetch and retry once.
      if (status === 403 && config && !config._csrfRetried && isMutating(config.method)) {
        config._csrfRetried = true;
        csrfToken = null;
        await ensureCsrfToken();
        if (csrfToken) {
          config.headers = config.headers || {};
          config.headers['X-CSRF-Token'] = csrfToken;
          return axios(config);
        }
      }
      return Promise.reject(err);
    }
  );
}

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true
});
const authApi = axios.create({
  baseURL: API_BASE || '/',
  withCredentials: true
});
attachCsrfInterceptor(api);
attachCsrfInterceptor(authApi);
attachUnauthorizedInterceptor(api);
attachUnauthorizedInterceptor(authApi);

// Surface a hook so the app can prime the token at login.
export const primeCsrf = () => fetchCsrfToken().catch(() => null);

// Drop the cached token when the user logs out so a subsequent
// login as a different user in the same tab doesn't pay a 403→refetch
// round trip on the first action.
export const clearCsrfCache = () => { csrfToken = null; };

// ─── Global 401 dispatcher ────────────────────────────────────────────
// When the session expires server-side, any in-flight API call returns
// 401. Subscribers (typically App) are notified so the UI can reset to
// the login screen instead of leaving the user staring at an empty
// page or a misleading "no data" state.
const unauthorizedListeners = new Set();
export const onUnauthorized = (fn) => {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
};

function attachUnauthorizedInterceptor(instance) {
  instance.interceptors.response.use(
    r => r,
    (err) => {
      if (err.response?.status === 401) {
        // Skip notifications for status-check endpoints — they're how
        // the app intentionally probes the session.
        const url = err.config?.url || '';
        const safe = url.includes('/auth/status') ||
                     url.includes('/auth/google/status') ||
                     url.includes('/csrf-token');
        if (!safe) {
          clearCsrfCache();
          for (const fn of unauthorizedListeners) {
            try { fn(); } catch (e) { /* ignore listener errors */ }
          }
        }
      }
      return Promise.reject(err);
    }
  );
}

// ─── Auth ──────────────────────────────────────────────────────────────────
export const getAuthStatusDirect = () => authApi.get('/auth/status').then(r => r.data);
export const logout = () => authApi.post('/auth/logout').then(r => r.data);
export const getXeroAuthUrl = () => `${API_BASE}/auth/xero`;
export const getXeroConsentUrl = () => authApi.get('/auth/xero-url').then(r => r.data.url);

// ─── Google Auth ───────────────────────────────────────────────────────────
export const getGoogleLoginUrl = () => `${API_BASE}/auth/google`;
export const getGoogleAuthStatus = () => authApi.get('/auth/google/status').then(r => r.data);
export const googleLogout = () => authApi.post('/auth/google/logout').then(r => r.data);

// ─── Clients ───────────────────────────────────────────────────
export const getClients = () => api.get('/clients').then(r => r.data.clients);

// ─── Reconciliation ────────────────────────────────────────────

export const startReconciliation = (tenantId, csvFile) => {
  const formData = new FormData();
  formData.append('tenantId', tenantId);
  formData.append('file', csvFile);
  return api.post('/reconcile/run', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30 * 1000
  }).then(r => r.data);
};

export const startCsvReconciliation = (statementFile, invoicesFile) => {
  const formData = new FormData();
  formData.append('statement', statementFile);
  formData.append('invoices', invoicesFile);
  return api.post('/reconcile/run-csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30 * 1000
  }).then(r => r.data);
};

export const pollReconciliationJob = (jobId) =>
  api.get(`/reconcile/status/${jobId}`, { timeout: 15 * 1000 }).then(r => r.data);

/**
 * Poll until done with a hard ceiling on attempts so the UI cannot spin
 * forever (eg. server died mid-job). Surfaces auth errors immediately
 * rather than retrying.
 */
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_ATTEMPTS = 150; // ~12 minutes at 5s intervals

async function pollUntilDone(jobId, onProgress) {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const status = await pollReconciliationJob(jobId);
        if (onProgress) onProgress(status);
        if (status.status === 'done') return resolve(status.result);
        if (status.status === 'error') return reject(new Error(status.error || 'Reconciliation failed'));
        if (attempts >= POLL_MAX_ATTEMPTS) {
          return reject(new Error('Reconciliation timed out — please try again or check the server logs.'));
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        const status = err.response?.status;
        // Auth failures must surface — keep silent retries only for transient network errors
        if (status === 401 || status === 403) return reject(err);
        if (attempts >= POLL_MAX_ATTEMPTS) return reject(err);
        setTimeout(poll, POLL_INTERVAL_MS + 3000);
      }
    };
    setTimeout(poll, 3000);
  });
}

export const runReconciliation = async (tenantId, csvFile, onProgress) => {
  const { jobId } = await startReconciliation(tenantId, csvFile);
  return pollUntilDone(jobId, onProgress);
};

export const runCsvReconciliation = async (statementFile, invoicesFile, onProgress) => {
  const { jobId } = await startCsvReconciliation(statementFile, invoicesFile);
  return pollUntilDone(jobId, onProgress);
};

export const approveMatch = (tenantId, invoiceId, amount, bankAccountId, date,
  invoiceAmountDue, invoiceContactId, invoiceType,
  extras = {}) =>
  api.post('/reconcile/approve', {
    tenantId, invoiceId, amount, bankAccountId, date,
    invoiceAmountDue, invoiceContactId, invoiceType,
    ...extras
  }).then(r => r.data);

export const explainMatch = (transaction, invoice = null) =>
  api.post('/reconcile/explain', { transaction, invoice }).then(r => r.data.explanation);

export const getBankAccounts = (tenantId) =>
  api.get('/reconcile/bank-accounts', { params: { tenantId } }).then(r => r.data.accounts);

export const getReconciliationHistory = (limit = 25) =>
  api.get('/reconcile/history', { params: { limit } }).then(r => r.data.jobs);

export const getPostedPayments = (limit = 100) =>
  api.get('/reconcile/posted', { params: { limit } }).then(r => r.data.payments);

/**
 * Download CSV via a fetch (so we can include the session cookie *and*
 * see HTTP errors) and trigger a blob download from the resulting bytes.
 */
export const downloadReconciliationCsv = async (jobId) => {
  const res = await fetch(`${API_BASE}/api/reconcile/export/${jobId}`, {
    credentials: 'include'
  });
  if (!res.ok) {
    let msg = `Download failed (HTTP ${res.status})`;
    try { const j = await res.json(); if (j.error) msg = j.error; } catch (_) {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `reconciliation-${jobId}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ─── Bookkeeping health check (read-only) ─────────────────────
// Health checks can take several minutes for a large Xero organisation. Start
// them as one server-side serialized job and poll a short-lived status request
// so a tab navigation cannot cancel the underlying check.
export const startHealthCheck = (tenantId) =>
  api.post('/health-check/run-async', { tenantId }, { timeout: 30 * 1000 }).then(r => r.data);

export const pollHealthCheck = (jobId) =>
  api.get(`/health-check/run/${encodeURIComponent(jobId)}`, { timeout: 15 * 1000 }).then(r => r.data);

const HEALTH_POLL_INTERVAL_MS = 3000;
const HEALTH_POLL_MAX_ATTEMPTS = 250; // 12.5 minutes, below server request ceiling

export async function runHealthCheck(tenantId, onStatus) {
  const { jobId } = await startHealthCheck(tenantId);
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const status = await pollHealthCheck(jobId);
        if (onStatus) onStatus(status);
        if (status.status === 'done') return resolve(status.result);
        if (status.status === 'error') return reject(new Error(status.error || 'Health check failed'));
        if (attempts >= HEALTH_POLL_MAX_ATTEMPTS) {
          return reject(new Error('Health check is still running. Please return to All clients shortly to view the saved result.'));
        }
        setTimeout(poll, HEALTH_POLL_INTERVAL_MS);
      } catch (err) {
        const httpStatus = err.response?.status;
        if (httpStatus === 401 || httpStatus === 403 || httpStatus === 404 || attempts >= HEALTH_POLL_MAX_ATTEMPTS) return reject(err);
        setTimeout(poll, HEALTH_POLL_INTERVAL_MS + 2000);
      }
    };
    setTimeout(poll, 500);
  });
}

export const getHealthSettings = (tenantId) =>
  api.get('/health-check/settings', { params: { tenantId } }).then(r => r.data.settings);

export const saveHealthSettings = (tenantId, settings) =>
  api.put('/health-check/settings', { tenantId, settings }).then(r => r.data.settings);

export const getHealthHistory = () =>
  api.get('/health-check/history').then(r => r.data.clients);

export const getSavedHealthReport = (reportId) =>
  api.get(`/health-check/report/${encodeURIComponent(reportId)}`).then(r => r.data);

export const deleteSavedHealthReport = (reportId) =>
  api.delete(`/health-check/report/${encodeURIComponent(reportId)}`).then(r => r.data);

// ─── Coding rules ──────────────────────────────────────────────
const rulesApi = axios.create({
  baseURL: `${API_BASE}/api/rules`,
  withCredentials: true
});
attachCsrfInterceptor(rulesApi);
attachUnauthorizedInterceptor(rulesApi);

export const listRules = (tenantId) =>
  rulesApi.get('/', { params: tenantId ? { tenantId } : {} }).then(r => r.data.rules);

export const createRule = (rule) =>
  rulesApi.post('/', rule).then(r => r.data.rule);

export const updateRule = (id, fields) =>
  rulesApi.put(`/${id}`, fields).then(r => r.data.rule);

export const deleteRule = (id) =>
  rulesApi.delete(`/${id}`).then(r => r.data);

// Read-only built-in matching strategies (audit / transparency).
export const listBuiltinRules = () =>
  api.get('/reconcile/builtin-rules').then(r => r.data.rules);

// ─── GDPR ─────────────────────────────────────────────────────
const meApi = axios.create({ baseURL: `${API_BASE}/api/me`, withCredentials: true });
attachCsrfInterceptor(meApi);
attachUnauthorizedInterceptor(meApi);

export const downloadMyData = async () => {
  const res = await fetch(`${API_BASE}/api/me/data`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `reconcile-data-${Date.now()}.json`;
  document.body.appendChild(link); link.click(); document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const deleteMyData = () => meApi.delete('/data').then(r => r.data);
